// Typed event bus. Single FIFO queue, single-threaded.
// Conforms to GDD Appendix A §A.3.

export type EventType =
  | 'spawn'
  | 'materialize'
  | 'death'
  | 'hit'
  | 'player_hit'
  | 'levelup_offer'
  | 'levelup_pick'
  | 'evolution'
  | 'pickup'
  | 'loot_crate'
  | 'dash'
  | 'surge_change'
  | 'phase_change'
  | 'wave_start'
  | 'wave_clear'
  | 'nova'
  | 'boss_spawn'
  | 'boss_telegraph'
  | 'boss_phase'
  | 'boss_defeated'
  | 'hazard'
  | 'pool_exhausted'
  | 'captain_skill'
  | 'game_over';

export interface GameEvent {
  type: EventType;
  payload: Record<string, unknown>;
  event_id: number;
  tick: number;
}

export class EventBus {
  private queue: GameEvent[] = [];
  private nextId = 1;
  private handlers: Array<(e: GameEvent) => void> = [];
  /** Last handler exception (debug overlay surfaces it on-device). */
  lastError: string | null = null;

  register(handler: (e: GameEvent) => void): void {
    this.handlers.push(handler);
  }

  emit(type: EventType, payload: Record<string, unknown>, tick: number): void {
    this.queue.push({ type, payload, event_id: this.nextId++, tick });
  }

  flush(tick: number): void {
    let i = 0;
    while (i < this.queue.length) {
      const e = this.queue[i];
      if (e.tick > tick) break;
      for (const h of this.handlers) {
        // A throwing handler must never wedge the bus: without this, the
        // exception aborts the flush BEFORE the splice, the queue keeps the
        // poisoned event forever, and every later event dies behind it
        // (symptom: all event-driven effects — deaths, banners, barks —
        // silently stop mid-run while the sim keeps going).
        try {
          h(e);
        } catch (err) {
          this.lastError = `${e.type}#${e.event_id}: ${String(err).slice(0, 160)}`;
          console.error(`[events] handler threw on ${e.type}#${e.event_id}`, e.payload, err);
        }
      }
      i++;
    }
    if (i > 0) {
      this.queue.splice(0, i);
    }
    if (this.queue.length > 4096) {
      if (typeof __DEV__ !== 'undefined' && __DEV__) {
        throw new Error(`Event queue overflow: ${this.queue.length}`);
      }
      this.queue.splice(0, this.queue.length - 4096);
    }
  }

  clear(): void {
    this.queue.length = 0;
  }
}
